import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, Fragment as _Fragment, createVNode as _createVNode, withModifiers as _withModifiers, createStaticVNode as _createStaticVNode } from "vue";
const _hoisted_1 = { class: "visualise-page" };
const _hoisted_2 = { class: "toolbar" };
const _hoisted_3 = {
    key: 0,
    class: "filter-pill"
};
const _hoisted_4 = { class: "referrals-toggle" };
const _hoisted_5 = {
    key: 0,
    class: "legend"
};
const _hoisted_6 = {
    key: 1,
    class: "state"
};
const _hoisted_7 = {
    key: 2,
    class: "state"
};
const _hoisted_8 = { class: "zoom-controls" };
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { GraphChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent } from 'echarts/components';
import VChart from 'vue-echarts';
import { useAdminReferrals } from '@/composables/useAdminReferrals';
import { useAdmin } from '@/composables/useAdmin';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ReferralsVisualisePage',
    setup(__props) {
        use([CanvasRenderer, GraphChart, TooltipComponent, LegendComponent]);
        const router = useRouter();
        const { getReferrals } = useAdminReferrals();
        const admin = useAdmin();
        const loading = ref(true);
        const referrals = ref([]);
        const chartRef = ref();
        const chartWrapRef = ref(null);
        const zoomLevel = ref(1);
        const now = Date.now();
        const filteredEmployerId = ref(null);
        const referralsOnly = ref(false);
        const searchText = ref('');
        const PALETTE = ['#7278FF', '#A3AEFF', '#413655', '#AA97E6', '#D5CBF3', '#FF7E7E', '#F5B993', '#B4D8D7'];
        function personName(customer) {
            const p = customer?.person;
            return [p?.firstName, p?.lastName].filter(Boolean).join(' ') || 'Unknown';
        }
        function employerName(id) {
            if (!id)
                return 'Unknown Employer';
            const found = admin.adminState.employers.find((e) => e.id === id);
            return found?.name ?? 'Unknown Employer';
        }
        // Returns a colour based on how recently the member joined. Null = use employer colour.
        function ageColour(createDateTime) {
            if (!createDateTime)
                return null;
            const days = (now - new Date(createDateTime).getTime()) / 86400000;
            if (days < 7)
                return '#4ACB64';
            if (days < 30)
                return '#A4D65E';
            if (days < 90)
                return '#F5C842';
            if (days < 180)
                return '#FF9A3C';
            return null;
        }
        function isActiveThisWeek(lastLogin) {
            if (!lastLogin)
                return false;
            return (now - new Date(lastLogin).getTime()) / 86400000 < 7;
        }
        // Faded = no employer linked (bank account linking needs a separate API field)
        function isLinked(customer) {
            return !!customer?.employerID;
        }
        function tooltipFor(c) {
            const days = c.createDateTime
                ? Math.floor((now - new Date(c.createDateTime).getTime()) / 86400000)
                : null;
            const age = days !== null ? (days < 1 ? 'Joined today' : `Joined ${days}d ago`) : '';
            const active = isActiveThisWeek(c.lastLoginDateTime) ? ' · Active this week' : '';
            const linked = isLinked(c) ? ' · Employer linked' : ' · No employer';
            return `<b>${personName(c)}</b><br/>${age}${active}${linked}`;
        }
        // Show the graph whenever there are members/employers to draw — referrals just
        // add edges on top, so an empty referral list should still render everyone.
        const hasData = computed(() => (admin.adminState.customers?.length ?? 0) > 0);
        const chartOption = computed(() => {
            if (!hasData.value)
                return {};
            const allCustomers = admin.adminState.customers ?? [];
            const employerIds = [...new Set(allCustomers.map((c) => c.employerID).filter(Boolean))];
            const categories = employerIds.map((id, i) => ({
                name: employerName(id),
                itemStyle: { color: PALETTE[i % PALETTE.length] },
            }));
            const employerCategoryIndex = (id) => employerIds.indexOf(id);
            // Member count per employer — drives hub size and label
            const memberCount = new Map();
            allCustomers.forEach((c) => {
                if (c.employerID)
                    memberCount.set(c.employerID, (memberCount.get(c.employerID) ?? 0) + 1);
            });
            const nodeMap = new Map();
            const links = [];
            // Employer nodes — when filtered, only show the selected employer
            const visibleEmployerIds = filteredEmployerId.value
                ? employerIds.filter(id => id === filteredEmployerId.value)
                : employerIds;
            visibleEmployerIds.forEach((id, i) => {
                const count = memberCount.get(id) ?? 0;
                // Scale hub size by member count (sqrt keeps big employers from dwarfing small ones)
                const size = Math.min(40 + Math.sqrt(count) * 4, 110);
                nodeMap.set('emp_' + id, {
                    id: 'emp_' + id,
                    name: `${employerName(id)} · ${count}`,
                    symbol: 'roundRect',
                    symbolSize: size,
                    category: i,
                    itemStyle: { color: PALETTE[i % PALETTE.length] },
                    label: {
                        show: true,
                        fontSize: 12,
                        fontWeight: 'bold',
                        color: '#ffffff',
                        position: 'bottom',
                        backgroundColor: PALETTE[i % PALETTE.length],
                        padding: [4, 10],
                        borderRadius: 20,
                        distance: 8,
                    },
                    _type: 'employer',
                    _employerId: id,
                });
            });
            // Ids that take part in any referral (as referrer or referee), plus maps to
            // walk a referral chain in both directions (who referred whom).
            const referralIds = new Set();
            const childrenOf = new Map(); // referrer id -> referee ids
            const parentOf = new Map(); // referee id -> referrer id
            referrals.value.forEach(r => {
                if (r.referrer?.id)
                    referralIds.add(r.referrer.id);
                if (r.referee?.id)
                    referralIds.add(r.referee.id);
                if (!r.referrer?.id || !r.referee?.id)
                    return;
                const ch = childrenOf.get(r.referrer.id) ?? [];
                ch.push(r.referee.id);
                childrenOf.set(r.referrer.id, ch);
                parentOf.set(r.referee.id, r.referrer.id);
            });
            // Collects the full referral chain a customer belongs to: every ancestor
            // (who referred them, recursively) and every descendant (who they referred).
            function collectChain(id, acc) {
                if (acc.has(id))
                    return;
                acc.add(id);
                const parent = parentOf.get(id);
                if (parent)
                    collectChain(parent, acc);
                (childrenOf.get(id) ?? []).forEach(child => collectChain(child, acc));
            }
            // Default view shows only employer hubs. Members appear once a hub is
            // selected — otherwise thousands of nodes make the graph unreadable.
            // "Referrals only" further drops members not part of any referral chain.
            // A name search surfaces matching members across all employers — and when a
            // match sits in a referral chain, the whole chain is pulled in so the full
            // referral path is drawn, not just the matched node.
            const search = searchText.value.trim().toLowerCase();
            let visibleCustomers;
            if (search) {
                const matchIds = new Set();
                allCustomers.forEach((c) => {
                    if (!personName(c).toLowerCase().includes(search))
                        return;
                    if (referralsOnly.value && !referralIds.has(c.id))
                        return;
                    collectChain(c.id, matchIds);
                });
                visibleCustomers = allCustomers.filter((c) => matchIds.has(c.id));
            }
            else if (filteredEmployerId.value) {
                visibleCustomers = allCustomers.filter((c) => c.employerID === filteredEmployerId.value &&
                    (!referralsOnly.value || referralIds.has(c.id)));
            }
            else {
                visibleCustomers = [];
            }
            // All customer nodes
            visibleCustomers.forEach((c) => {
                const catIdx = employerCategoryIndex(c.employerID);
                const colour = ageColour(c.createDateTime) ?? PALETTE[catIdx >= 0 ? catIdx % PALETTE.length : 0];
                const active = isActiveThisWeek(c.lastLoginDateTime);
                const linked = isLinked(c);
                nodeMap.set(c.id, {
                    id: c.id,
                    name: personName(c),
                    symbolSize: 18,
                    category: catIdx >= 0 ? catIdx : 0,
                    itemStyle: {
                        color: colour,
                        opacity: linked ? 1 : 0.45,
                        borderColor: active ? '#00C853' : 'transparent',
                        borderWidth: active ? 3 : 0,
                    },
                    label: { show: false, fontSize: 10, position: 'bottom' },
                    tooltip: { formatter: () => tooltipFor(c) },
                    _type: 'member',
                    _customerId: c.id,
                });
                if (c.employerID && visibleEmployerIds.includes(c.employerID)) {
                    links.push({ source: 'emp_' + c.employerID, target: c.id, lineStyle: { opacity: 0.2, color: '#bbb' } });
                }
            });
            const visibleIds = new Set(visibleCustomers.map((c) => c.id));
            function countDescendants(id, visited = new Set()) {
                if (visited.has(id))
                    return 0;
                visited.add(id);
                return (childrenOf.get(id) ?? []).reduce((s, c) => s + 1 + countDescendants(c, visited), 0);
            }
            referrals.value.forEach(r => {
                const referrer = r.referrer;
                const referee = r.referee;
                if (!referrer || !referee)
                    return;
                if (!visibleIds.has(referrer.id) || !visibleIds.has(referee.id))
                    return;
                const desc = countDescendants(referrer.id);
                const referrerNode = nodeMap.get(referrer.id);
                if (referrerNode)
                    referrerNode.symbolSize = Math.min(18 + desc * 10, 60);
                const existingIdx = links.findIndex(l => l.target === referee.id && l.source.startsWith('emp_'));
                if (existingIdx >= 0)
                    links.splice(existingIdx, 1);
                links.push({ source: referrer.id, target: referee.id, lineStyle: { opacity: 0.7, width: 2, color: '#413655' } });
            });
            const nodes = [...nodeMap.values()];
            return {
                backgroundColor: '#f5f5f8',
                tooltip: { trigger: 'item' },
                series: [{
                        type: 'graph',
                        layout: 'force',
                        roam: 'move',
                        draggable: true,
                        categories,
                        nodes,
                        links,
                        force: {
                            repulsion: 400,
                            gravity: 0.05,
                            edgeLength: [80, 180],
                            // Animating hundreds of nodes never settles and just jitters — let
                            // large expansions snap to a stable layout instead.
                            layoutAnimation: nodes.length <= 80,
                        },
                        label: { show: true, color: '#413655' },
                        lineStyle: { color: '#aaa', curveness: 0.1 },
                        emphasis: { focus: 'adjacency', label: { show: true }, lineStyle: { width: 3 } },
                    }],
            };
        });
        function onWheel(e) {
            // Trackpad pinch arrives as ctrl+wheel with a stream of small deltas;
            // a mouse scrollwheel sends single events with deltaY around ±100.
            // Scale the zoom by delta size so both feel right.
            const sensitivity = e.ctrlKey ? 0.01 : 0.002;
            zoom(Math.exp(-e.deltaY * sensitivity));
        }
        function zoom(factor) {
            zoomLevel.value = Math.min(Math.max(zoomLevel.value * factor, 0.1), 20);
            chartRef.value?.setOption({ series: [{ zoom: zoomLevel.value }] });
        }
        function toggleFullscreen() {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            }
            else {
                chartWrapRef.value?.requestFullscreen();
            }
        }
        function resetZoom() {
            zoomLevel.value = 1;
            chartRef.value?.setOption(chartOption.value, true);
        }
        // Clicking blank canvas clears the employer filter (zr:click fires for every
        // click; params.target is only set when a node/edge was hit).
        function onCanvasClick(params) {
            if (!params.target && filteredEmployerId.value) {
                filteredEmployerId.value = null;
                zoomLevel.value = 1;
            }
        }
        function onNodeClick(params) {
            if (params.dataType !== 'node')
                return;
            if (params.data._type === 'member') {
                router.push({ path: '/admin/customers', query: { customerId: params.data._customerId } });
            }
            else if (params.data._type === 'employer') {
                const id = params.data._employerId;
                if (filteredEmployerId.value === id) {
                    // Second click — go to employer admin
                    router.push({ path: '/admin/employers', query: { employerId: id } });
                }
                else {
                    // First click — filter to this employer
                    filteredEmployerId.value = id;
                    zoomLevel.value = 1;
                    // Clear the adjacency-blur ECharts applies on click, else chained
                    // referees (not direct neighbours of the employer) stay faded
                    chartRef.value?.dispatchAction({ type: 'downplay', seriesIndex: 0 });
                }
            }
        }
        onMounted(() => {
            admin.getEmployers();
            admin.getCustomers();
            getReferrals((result) => {
                referrals.value = result ?? [];
                loading.value = false;
            });
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("button", {
                        class: "back-btn",
                        onClick: _cache[0] || (_cache[0] = ($event) => (_unref(router).push('/admin/referrals')))
                    }, "← Referrals"),
                    (filteredEmployerId.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createTextVNode(_toDisplayString(employerName(filteredEmployerId.value)) + " ", 1),
                            _createElementVNode("button", {
                                onClick: _cache[1] || (_cache[1] = ($event) => (filteredEmployerId.value = null))
                            }, "✕")
                        ]))
                        : _createCommentVNode("", true),
                    (filteredEmployerId.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _withDirectives(_createElementVNode("input", {
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((searchText).value = $event)),
                                type: "text",
                                class: "search-box",
                                placeholder: "Search by name…"
                            }, null, 512), [
                                [_vModelText, searchText.value]
                            ]),
                            _createElementVNode("label", _hoisted_4, [
                                _withDirectives(_createElementVNode("input", {
                                    type: "checkbox",
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((referralsOnly).value = $event))
                                }, null, 512), [
                                    [_vModelCheckbox, referralsOnly.value]
                                ]),
                                _cache[6] || (_cache[6] = _createTextVNode(" Show referrals only "))
                            ])
                        ], 64))
                        : _createCommentVNode("", true)
                ]),
                (filteredEmployerId.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[7] || (_cache[7] = [
                        _createStaticVNode("<span class=\"legend-item\" data-v-8c378fb2><span class=\"dot\" style=\"background:#4ACB64;\" data-v-8c378fb2></span> New (&lt;1w)</span><span class=\"legend-item\" data-v-8c378fb2><span class=\"dot\" style=\"background:#A4D65E;\" data-v-8c378fb2></span> &lt;1m</span><span class=\"legend-item\" data-v-8c378fb2><span class=\"dot\" style=\"background:#F5C842;\" data-v-8c378fb2></span> &lt;3m</span><span class=\"legend-item\" data-v-8c378fb2><span class=\"dot\" style=\"background:#FF9A3C;\" data-v-8c378fb2></span> &lt;6m</span><span class=\"legend-item\" data-v-8c378fb2><span class=\"dot ring\" data-v-8c378fb2></span> Active this week</span><span class=\"legend-item\" data-v-8c378fb2><span class=\"dot faded\" data-v-8c378fb2></span> No employer</span>", 6)
                    ])))
                    : _createCommentVNode("", true),
                (loading.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, "Building graph…"))
                    : (!hasData.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, "No data to visualise."))
                        : _createCommentVNode("", true),
                _createElementVNode("div", {
                    ref_key: "chartWrapRef",
                    ref: chartWrapRef,
                    class: "chart-wrap",
                    onWheel: _withModifiers(onWheel, ["prevent"])
                }, [
                    _createElementVNode("div", _hoisted_8, [
                        _createElementVNode("button", {
                            onClick: _cache[4] || (_cache[4] = ($event) => (zoom(1.3))),
                            title: "Zoom in"
                        }, "＋"),
                        _createElementVNode("button", {
                            onClick: _cache[5] || (_cache[5] = ($event) => (zoom(0.75))),
                            title: "Zoom out"
                        }, "－"),
                        _createElementVNode("button", {
                            onClick: resetZoom,
                            title: "Reset"
                        }, "⊡"),
                        _createElementVNode("button", {
                            onClick: toggleFullscreen,
                            title: "Full screen"
                        }, "⛶")
                    ]),
                    _createVNode(_unref(VChart), {
                        ref_key: "chartRef",
                        ref: chartRef,
                        option: chartOption.value,
                        autoresize: "",
                        class: "chart",
                        onClick: onNodeClick,
                        "onZr:click": onCanvasClick
                    }, null, 8, ["option"])
                ], 544)
            ]));
        };
    }
});
